import { useState, useEffect, useCallback } from "react";

export interface FilterConfig<T> {
    fields: (keyof T)[];
    type: "string" | "exact";
}

export const useFiltering = <T extends object>(
    initialData: T[],
    filterConfig: { [key: string]: FilterConfig<T> }
) => {
    const [filters, setFilters] = useState<Record<string, string>>({});
    const [filteredData, setFilteredData] = useState<T[]>(initialData);

    useEffect(() => {
        setFilteredData(
            initialData.filter((item) =>
                Object.entries(filters).every(([key, value]) => {
                    if (!value) {
                        return true;
                    }

                    const config = filterConfig[key];

                    if (!config) {
                        return true;
                    }

                    return config.fields.some((field) => {
                        const itemValue = item[field];

                        if (itemValue === null || itemValue === undefined) {
                            return false;
                        }

                        if (config.type === "string") {
                            return String(itemValue).toLowerCase().includes(value.toLowerCase());
                        }

                        if (config.type === "exact") {
                            return String(itemValue) === value;
                        }

                        return false;
                    });
                })
            )
        );
    }, [initialData, filters, filterConfig]);

    const setFilterValue = useCallback((filterName: string, value: string) => {
        setFilters((prevFilters) => ({
            ...prevFilters,
            [filterName]: value,
        }));
    }, []);

    const handleFilterChange = useCallback(
        ({ target: { name, value } }: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
            setFilterValue(name, value),
        [setFilterValue]
    );

    const getFilterValue = useCallback(
        (filterName: string): string => filters[filterName] || "",
        [filters]
    );

    const resetFilters = useCallback(() => {
        setFilters({});
    }, []);

    return {
        filteredData,
        handleFilterChange,
        getFilterValue,
        setFilterValue,
        resetFilters,
        filters,
    };
};
